// @flow
import React, { useState } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import {
  TripAdvisorAwards,
  type AwardProps,
} from 'Components/widgets/tripadvisor-awards/TripAdvisorAwards.jsx';
import {
  TripAdvisorRatings,
  type TripAdvisorRatingsProps,
} from 'Components/widgets/tripadvisor-ratings/TripAdvisorRatings.jsx';
import {
  TripAdvisorReviews,
  type ReviewProps,
} from 'Components/widgets/tripadvisor-reviews/TripAdvisorReviews.jsx';

export type TripAdvisorNarrowProps = {
  className?: string,
  awards?: Array<AwardProps>,
  awardsLabel?: string,
  ratings?: TripAdvisorRatingsProps,
  ratingsLabel?: string,
  reviews?: Array<ReviewProps>,
  reviewsLabel?: string,
};

export function TripAdvisorNarrow(props: TripAdvisorNarrowProps) {
  const {
    awards = [],
    awardsLabel,
    className,
    ratings = {},
    ratingsLabel,
    reviews,
    reviewsLabel,
  } = props;

  const [position, setPosition] = useState(0);

  const hasAwards = !isEmpty(awards);
  const hasRatings = !isEmpty(ratings.subratings);
  const hasReviews = !isEmpty(reviews);

  if (!hasAwards && !hasRatings && !hasReviews) return null;

  const items = [];
  const nav = [];

  if (hasReviews) {
    items.push(
      <TripAdvisorReviews
        key="reviews"
        className="w-full"
        // $FlowFixMe
        reviews={reviews}
        maxLengthOfReview={150}
      />,
    );
    nav.push(reviewsLabel);
  }

  if (hasRatings) {
    items.push(
      <TripAdvisorRatings
        {...ratings}
        key="ratings"
        className="w-full"
        subratings={ratings.subratings}
      />,
    );
    nav.push(ratingsLabel);
  }

  if (hasAwards) {
    items.push(<TripAdvisorAwards key="awards" className="w-full self-center" awards={awards} />);
    nav.push(awardsLabel);
  }

  return (
    <section className={classnames(className, 'mx-auto')} style={{ maxWidth: '37.5rem' }}>
      <div className="border-1 border-gray-light rounded-tiny overflow-hidden">
        <div
          className="flex transition-transform"
          style={{ transform: `translate(${position * -100}%, 0)` }}
        >
          {items.map((item, idx) => (
            <div
              key={idx}
              aria-hidden={position !== idx}
              className={classnames('max-w-full min-w-full overflow-hidden p-3 flex', {
                invisible: position !== idx,
              })}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <div className="flex relative">
        <div
          className="absolute arrow-up arrow-white arrow-bg arrow-bg-blue border-t-1 border-blue"
          style={{
            width: `${100 / nav.length}%`,
            margin: `0 ${(100 / nav.length) * position}%`,
            transition: 'all .25s ease-in .2s',
          }}
        />
        {nav.map((button, idx) => (
          <button
            className={classnames(
              'reset-button flex-1 font-bold py-5 text-base text-center',
              { 'text-gray-medium': idx !== position },
              { 'text-turquoise': idx === position },
            )}
            onClick={() => setPosition(idx)}
            type="button"
            key={button}
          >
            <span className={classnames('px-2 block', { 'rtl:border-l-1': idx > 0 })}>
              {button}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
